import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Download } from "lucide-react";
import type { AssistantArtifactPart } from "../../../chat/types";
import { AnimatedCard } from "../../Common/AnimatedCard";
import { ArtifactExportMenu } from "./ArtifactExportMenu";
import { TABLE_EXPORT_FORMATS } from "./artifactExportFormats";
import { readArtifactBody } from "./artifactTable";
import { TRUNCATION_WARNING } from "../../../lib/export/provenance";

/** Rows rendered inline. A cap keeps a 500-row artifact from owning the chat
 *  transcript; the export menu is the escape hatch and the row-count line
 *  below makes the elision visible instead of silent. */
const ROW_CAP = 20;

/** Elision marker `_truncate_tool_result_content` splices into a tool result
 *  before the model ever sees it (`agent_loop.py:194-226`). Production bodies
 *  carry the literal U+2026 ellipsis character, never its JSON escape
 *  sequence: the tool argument is decoded long before the artifact writer
 *  stores the string. Mirrors `documents/normalize.py:29`. */
const TRUNCATION_MARKER = "[…truncated";

/** Cell text is clamped to this many lines. A 13-column invoice table with
 *  prose columns (Reason, Reasoning, Sources) otherwise wraps into rows a
 *  screen tall; the full string stays reachable via the cell's `title` and
 *  the export. */
const CELL_MAX_LINES = 3;

/** At or under this length a value (date, number, severity, qty) is never
 *  wrapped: splitting "2026-07-01" across lines is unreadable. */
const SHORT_CELL_MAX_CHARS = 24;

const BODY_CELL_SX = {
  verticalAlign: "top",
  maxWidth: 280,
} as const;

// A floor on prose columns: without it the browser fits the table by
// shrinking text columns to a few characters (splitting words) instead of
// letting the scroll container scroll.
const LONG_CELL_SX = { ...BODY_CELL_SX, minWidth: 160 } as const;
const SHORT_CELL_SX = { ...BODY_CELL_SX, whiteSpace: "nowrap" } as const;

const CELL_CLAMP_SX = {
  display: "-webkit-box",
  WebkitLineClamp: CELL_MAX_LINES,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  // Only breaks a token that cannot fit a line on its own (ids, URLs), so
  // ordinary words are never split.
  overflowWrap: "break-word",
} as const;

export function ArtifactTableCard({ part }: { part: AssistantArtifactPart }) {
  // The server decides what is exportable; this mirrors it. Gating on "a
  // top-level JSON array parsed", as this did, hid the menu on {header, rows},
  // array-of-arrays, CSV and markdown bodies — four of the five shapes the
  // route answers 200 for.
  // Once per body, not per render (ELASV1-3514).
  const body = useMemo(() => readArtifactBody(part.text), [part.text]);
  const headers = body.status === "table" ? body.headers : [];
  const rows = body.status === "table" ? body.rows : [];
  const preview = rows.slice(0, ROW_CAP);
  const hasTable = body.status === "table";
  const truncatedSource = part.text.includes(TRUNCATION_MARKER);

  // Offered on both surfaces, by different means. In the SPA a plain link is
  // enough: same origin, session cookie, and the browser applies the server's
  // Content-Disposition. The embed has no cookie and is cross-origin, so a
  // relative href would resolve against the host page and arrive
  // unauthenticated — there the bytes are fetched with the bearer instead.
  // Hiding the menu in the embed, as this did, hid the feature from the whole
  // of it: EOLAS-V1 mounts Artie as a
  // pure iframe, so every ERP user is an embed user.
  //
  // Offering the menu only once a table actually parsed keeps the affordance
  // off the one body shape the route answers with a 409.
  // `opaque` is offered too: a CSV or markdown body is not previewable here but
  // the server's own readers render it. Only `unexportable` — valid JSON in no
  // table shape — is the body the route actually 409s on.
  const canExport =
    part.status === "complete" && body.status !== "unexportable";

  return (
    <AnimatedCard variant="sm" sx={{ my: 1, p: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        {part.title && (
          <Typography variant="caption" sx={{ flex: 1 }}>
            {part.title}
          </Typography>
        )}
        <ArtifactExportMenu
          artifactId={part.artifact_id}
          title={part.title}
          formats={TABLE_EXPORT_FORMATS}
          disabled={!canExport}
        />
      </Box>
      {hasTable ? (
        <>
          {/* Scrolls inside the card: without it a wide table spills its
              columns past the card's right edge. */}
          <Box
            data-testid="artifact-table-scroll"
            sx={{ overflowX: "auto", maxWidth: "100%" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headers.map((h, c) => (
                    <TableCell key={h || c} sx={{ whiteSpace: "nowrap" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((r, i) => (
                  <TableRow key={i}>
                    {headers.map((h, c) => {
                      const text = String(r[c] ?? "");
                      const isShort = text.length <= SHORT_CELL_MAX_CHARS;
                      return (
                        <TableCell
                          key={h || c}
                          title={text}
                          data-cell={isShort ? "short" : "long"}
                          sx={isShort ? SHORT_CELL_SX : LONG_CELL_SX}
                        >
                          <Box sx={CELL_CLAMP_SX}>{text}</Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          {rows.length > preview.length && (
            // Scoped to THIS table, deliberately. The tool result was capped
            // before the model transcribed it, so the source row count is
            // recorded nowhere and "of N total" would be a claim we cannot
            // make.
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
            >
              Showing {preview.length} of {rows.length} rows in this table
            </Typography>
          )}
          {truncatedSource && (
            // Annotate, never block: the marker is a substring heuristic, and
            // withholding someone's own data on a maybe is the worse failure.
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 0.5, color: "warning.main" }}
            >
              {TRUNCATION_WARNING}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {body.status === "opaque"
            ? // Honest about which of the two this is: the server can read a CSV
              // or markdown body even though nothing here can, so the Export
              // menu above is live and saying "no data" would contradict it.
              "Preview unavailable for this format — use Export to download it."
            : "No tabular data."}
        </Typography>
      )}
    </AnimatedCard>
  );
}
